// billingPlans — the pure derivation layer for the Billing & Plans page.
//
// Turns the entitlement engine's catalog (@charitystack/entitlements CATALOG)
// plus the UI's two selections (billing cycle, fee mode) into render-ready
// view-models. This is where the Done-Contract correctness lives, so it is
// deliberately framework-free and unit-tested against the engine:
//
//   - ALL fee rates come from CATALOG.fees (platformFeeCovered /
//     platformFeeUncovered / processingFee). NEVER hard-coded. The design
//     mockup hard-codes "6%", "4%", etc.; we format them from the engine's
//     exact decimal strings ("0.0600" → "6%") so a YAML/engine change flows
//     through automatically and a hard-coded copy can never drift.
//   - ALL prices come from CATALOG.subscriptionPrices (integer cents) →
//     formatted for display. NEVER hard-coded.
//   - The funnel PRESENTATION (taglines, hero lines, feature-group labels and
//     copy) is the only thing defined here as static metadata — it is editorial
//     framing, not entitlement data. Which features a tier HAS is read from the
//     engine grid via the EntitlementsProvider; this file only orders/labels
//     them for the cards.
//
// Self-serve checkout is offered for BASE / PLUS / PRO. STARTER ($0) renders as
// "current/free, no checkout"; ENTERPRISE renders "Contact sales" (null price).
// This mirrors the backend's PRICING_SOLD_PLANS eligibility boundary.

import { CATALOG, PLAN_TO_TIER, type Plan, type Tier } from '@charitystack/entitlements';
import type { PlanPresentation } from './billingPlanContent';

export type BillingCycle = 'monthly' | 'yearly';
export type FeeMode = 'commitment' | 'standard';

// The catalog 'custom' sentinel (Enterprise negotiated pricing, no list rate).
const CUSTOM_FEE_SENTINEL = 'custom';

// Plans shown as CARDS, in left-to-right render order. Only the three self-serve
// rungs are cards (Plus → Base → Starter, highest tier first); PRO and
// ENTERPRISE are sales-assisted and live in the "Scaling past Plus?" strip, not
// as cards. (PRO remains in CHECKOUTABLE_PLANS because the backend still accepts
// a PRO checkout — we simply don't surface a self-serve PRO card.)
// NOTE: this array's order IS the card order — BillingPlansPage .map()s it
// directly. The "Most popular" highlight follows Plus by plan identity
// (isRecommended={plan === 'PLUS'}), not by position, so reordering is safe.
export const DISPLAYED_PLANS: Plan[] = ['PLUS', 'BASE', 'STARTER'];

// The plans the grid actually renders for a given org (PE-1851). Pro is an
// INTERNAL-ONLY tier — onboarded case by case, never sold self-serve — so the
// PRO card exists exactly for the org that is already on it: a PRO org sees a
// 4-card grid with its own tier first (current-plan treatment); every other
// org gets DISPLAYED_PLANS unchanged. Keeping DISPLAYED_PLANS itself untouched
// is the structural never-surface-Pro guard — do not add PRO to that array.
export function displayedPlansFor(currentPlan: Plan): Plan[] {
  return currentPlan === 'PRO' ? ['PRO', ...DISPLAYED_PLANS] : DISPLAYED_PLANS;
}

// The self-serve checkable tiers as a TYPE — the narrowing `isCheckoutable`
// produces, so a checked plan is assignable to the checkout contract's
// `CheckoutPlan` ('BASE'|'PLUS'|'PRO') without a cast.
export type CheckoutablePlan = Extract<Plan, 'BASE' | 'PLUS' | 'PRO'>;

// Self-serve checkable tiers — the ONLY ones that render a checkout CTA. Mirrors
// backends/pricing PRICING_SOLD_PLANS (BASE/PLUS/PRO). STARTER + ENTERPRISE are
// excluded (the backend rejects them; PE-1628 AC-7).
export const CHECKOUTABLE_PLANS: ReadonlySet<Plan> = new Set<Plan>([
  'BASE',
  'PLUS',
  'PRO',
]);

// Type guard: narrows a Plan to a CheckoutablePlan so callers can pass the
// result straight to the checkout contract after the check.
export function isCheckoutable(plan: Plan): plan is CheckoutablePlan {
  return (CHECKOUTABLE_PLANS as ReadonlySet<string>).has(plan);
}

// The engine's canonical plan→tier map is the single source of truth — never a
// local copy (which would silently drift if a tier is added/renamed upstream).
// Same pattern the EntitlementsProvider uses.
const PLAN_TO_TIER_LOCAL: Record<Plan, Tier> = PLAN_TO_TIER;

// --- Formatting helpers (engine values → display) --------------------------

// "0.0600" (decimal-string fraction from the engine) → "6%". Returns null for
// the 'custom' sentinel (Enterprise) so callers render "Custom" rather than NaN.
// Also returns null for empty/blank/non-numeric input: a money formatter must
// NOT silently render "0%" for missing data (Number('') === 0 would do exactly
// that), so we reject anything that isn't a real numeric string.
export function formatFeePct(rate: string): string | null {
  if (rate === CUSTOM_FEE_SENTINEL) return null;
  if (typeof rate !== 'string' || rate.trim() === '') return null;
  const n = Number(rate);
  if (!Number.isFinite(n)) return null;
  const pct = n * 100;
  // Trim trailing zeros: 6, 4.5 — not 6.00.
  return `${Number(pct.toFixed(2))}%`;
}

// Integer cents → "$39" (whole dollars when even) or "$39.50" otherwise. null
// price (Enterprise) → null; 0 (Starter) → "$0".
export function formatPriceCents(cents: number | null): string | null {
  if (cents == null) return null;
  const dollars = cents / 100;
  const str =
    Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2);
  return `$${str}`;
}

// The SMS credits included per month for a plan, read straight from the engine
// (CATALOG.limits.sms_included_per_month). Returns 0 for the 'custom' sentinel
// (Enterprise) or any other non-numeric value — mirrors the type-guard pattern
// already used by MessagingCredits.tsx's PLUS_INCLUDED_CREDITS.
export function planSmsIncluded(plan: Plan): number {
  const tier = PLAN_TO_TIER_LOCAL[plan];
  const raw = CATALOG.limits.sms_included_per_month[tier];
  return typeof raw === 'number' ? raw : 0;
}

// --- Per-plan price/fee derivation (engine-sourced) ------------------------

// The per-month price for a plan at a cycle, in integer cents (the engine's
// native unit). yearly = the lower per-month rate billed annually.
export function planPriceCents(plan: Plan, cycle: BillingCycle): number | null {
  const tier = PLAN_TO_TIER_LOCAL[plan];
  const prices = CATALOG.subscriptionPrices;
  return cycle === 'yearly'
    ? prices.yearlyMonthlyUsd[tier]
    : prices.monthlyUsd[tier];
}

// The platform-fee rate that applies to a plan under a fee mode, as a display
// string ("6%") or null for custom/enterprise. Commitment → covered rate (what
// a donor pays IF they cover; org pays $0 when they don't). Standard → the
// uncovered rate the ORG absorbs on every gift.
export function planPlatformFee(plan: Plan, feeMode: FeeMode): string | null {
  const tier = PLAN_TO_TIER_LOCAL[plan];
  const rate =
    feeMode === 'commitment'
      ? CATALOG.fees.platformFeeCovered[tier]
      : CATALOG.fees.platformFeeUncovered[tier];
  return formatFeePct(rate);
}

// The flat processing fee for a plan, as a display string ("3%") or null.
export function planProcessingFee(plan: Plan): string | null {
  const tier = PLAN_TO_TIER_LOCAL[plan];
  return formatFeePct(CATALOG.fees.processingFee[tier]);
}

// Free-trial length in whole DAYS for a plan, read straight from the engine
// (CATALOG.trialDays — the SAME value the pricing backend attaches as Stripe's
// subscription_data.trial_period_days). 0 = no trial. NEVER hard-coded here, so
// a YAML/engine trial change flows through to the card copy automatically and
// the displayed length can't drift from what Stripe actually charges.
//
// NOTE on eligibility: the engine value is the CATALOG length, not the
// org-specific entitlement. The pricing backend's one-trial-per-org guard
// (resolveEffectiveTrialDays) can zero this at checkout for an org that already
// provisioned. For v1 the card shows the catalog trial whenever it's > 0 on an
// upgrade path; a returning-org gate is a follow-up (the copy is honest for the
// common first-upgrade case and the backend still enforces the real length).
export function planTrialDays(plan: Plan): number {
  const tier = PLAN_TO_TIER_LOCAL[plan];
  return CATALOG.trialDays[tier] ?? 0;
}

// The largest annual saving across the paid self-serve tiers, as a whole-number
// percent (e.g. 26). DERIVED from the engine's prices — not a hard-coded string —
// so the "Save up to N%" badge can never drift from CATALOG.subscriptionPrices.
// saving(tier) = 1 - (yearlyPerMonth*12) / (monthly*12) = 1 - yearlyPerMonth/monthly.
export function maxAnnualSavingsPct(): number {
  let best = 0;
  for (const plan of CHECKOUTABLE_PLANS) {
    const monthly = planPriceCents(plan, 'monthly');
    const yearly = planPriceCents(plan, 'yearly');
    if (monthly && yearly && monthly > 0) {
      const saving = 1 - yearly / monthly;
      if (saving > best) best = saving;
    }
  }
  return Math.round(best * 100);
}

// Rewrite any bullet's `text` that embeds a usage-limit number
// (`smsIncludedTier` today; extend the same way for a future limit) with the
// LIVE catalog value, so the presentation config's literal can never silently
// drift from the entitlement engine (PE-1855 QA fix — Pro's SMS bullet showed a
// stale "4,000" after the catalog moved to 5,000). Pure + non-destructive, same
// shape as stripSmsFromPresentation: call it on every presentation before it
// reaches PlanCard.
export function resolveDynamicCopy(
  presentation: PlanPresentation,
): PlanPresentation {
  return {
    ...presentation,
    groups: presentation.groups.map((g) => ({
      ...g,
      items: g.items.map((it) =>
        it.smsIncludedTier
          ? {
              ...it,
              text: it.text.replace(
                /^[\d,]+/,
                planSmsIncluded(it.smsIncludedTier).toLocaleString(),
              ),
            }
          : it,
      ),
    })),
  };
}

// --- Funnel presentation metadata (editorial, NOT entitlement data) --------
//
// The card COPY (taglines, hero lines, feature groups/bullets) + the SMS-gating
// transform now live in their own editable file — see billingPlanContent.ts
// (with a "HOW TO EDIT" header). Re-exported here so existing imports
// (`from 'utils/billingPlans'`) are unchanged. This file stays the numbers/
// derivation layer; that file is the copy.
export {
  PLAN_PRESENTATION,
  stripSmsFromPresentation,
  type PlanFeatureItem,
  type PlanFeatureGroup,
  type PlanPresentation,
} from './billingPlanContent';
